import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import { faFolderOpen, faUsers } from "@fortawesome/free-solid-svg-icons";
import "./curatedShortlistedSearch.css";

import FolderGrid from "./folderGrid";
import CandidateGrid from "./CandidateGrid";

import { ClientFolderService } from "../../../services/clientFolderService";
import { ClientService } from "../../../services/clientService";
import { decodeJWT } from "../../../services/candidateService";

import candidate_landing_page_image from "../../../assets/images/im_Candidate Landing page empty Search.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import JobListingService from "../../../services/JobListingService";

const CuratedShortlistedSearch = () => {
    const navigate = useNavigate();
    const PAGE_SIZE = 12;

    const decoded = decodeJWT(localStorage.getItem("authToken"));
    const clientId = _.get(decoded, "clientId", "");

    const [activeTab, setActiveTab] = useState("curated");
    const [applicantJobStatus, setApplicantJobStatus] = useState("ACTIVE");
    const [isRestoring, setIsRestoring] = useState(true);

    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);

    const [candidates, setCandidates] = useState([]);
    const [load, setLoad] = useState(false);
    const [folderPage, setFolderPage] = useState(0);
    const [folderTotalPages, setFolderTotalPages] = useState(0);

    // Candidate pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [searchInput, setSearchInput] = useState("");
    const [searchKeyword, setSearchKeyword] = useState("");

    const [totalEntries, setTotalEntries] = useState(0);
    const [startEntry, setStartEntry] = useState(0);
    const [endEntry, setEndEntry] = useState(0);

    const [totalViews, setTotalViews] = useState(0);
    const [viewedCount, setViewedCount] = useState(0);

    const [activeFolderMenu, setActiveFolderMenu] = useState(null);
    const [showRenamePopup, setShowRenamePopup] = useState(false);
    const [showDeletePopup, setShowDeletePopup] = useState(false);
    const [folderToRename, setFolderToRename] = useState(null);
    const [folderToDelete, setFolderToDelete] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [reloadCandidatesKey, setReloadCandidatesKey] = useState(0);
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {

        if (isRestoring || !clientId || activeTab === "applicants") return;

        if (!sessionStorage.getItem("curatedShortlistedState")) {
            setSelectedFolder(null);
            setCandidates([]);
            setCurrentPage(0);
        }


        const loadFolders = async () => {
            setLoad(true);
            try {
                const res =
                    activeTab === "curated"
                        ? await ClientFolderService.listCuratedFolders(
                            clientId,
                            "CURATED",
                            folderPage,
                            PAGE_SIZE
                        )
                        : await ClientFolderService.listShortlistedFolders(
                            clientId,
                            "SHORTLISTED",
                            folderPage,
                            PAGE_SIZE
                        );

                const list = (res?.data?.folders || []).map(f => ({
                    folderId: f.folderId,
                    folderName: f.name || f.folderName
                }));

                setFolders(list);
                const totalFolderCount = res?.data?.totalCount || 0;
                setFolderTotalPages(Math.ceil(totalFolderCount / PAGE_SIZE));
            } catch (err) {
                console.error("Error loading folders", err);
                setFolders([]);
            } finally {
                setLoad(false);
            }
        };

        loadFolders();
    }, [activeTab, clientId, isRestoring, folderPage]);



    useEffect(() => {
        if (activeTab !== "applicants") return;

        const loadJobs = async () => {
            setLoad(true);

            try {

                const res = await JobListingService.getJobsByStatus(
                    applicantJobStatus,
                    folderPage,
                    PAGE_SIZE
                );

                const jobs = (res.data.jobListings || []).map(job => ({
                    folderId: job.id,
                    folderName: job.jobListing?.jobTitle || ""
                }));

                setFolders(jobs);
                setFolderTotalPages(res.data.pageCount);
            } catch (err) {
                console.error(err);
                setFolders([]);
            } finally {
                setLoad(false);
            }
        };

        loadJobs();
    }, [activeTab, folderPage, applicantJobStatus]);



    useEffect(() => {
        // if (!selectedFolder || !clientId) return;
        if (!selectedFolder) return;

        const loadCandidates = async () => {

            if (activeTab === "applicants") {
                setLoad(true);

                try {
                    const res = await JobListingService.getApplicants(
                        selectedFolder.folderId,
                        currentPage,
                        PAGE_SIZE,
                        searchKeyword
                    );

                    const mapped = (res.data.applicants || []).map(item => {
                        const candidate = item.candidate;
                        const personal = candidate?.candidate?.personalDetails || {};
                        const work = candidate?.candidate?.workExperience || {};
                        const compensation =
                            candidate?.candidate?.compensationDetails || {};

                        return {
                            candidateId: candidate.candidateId,
                            salutation: personal.salutation || "",
                            firstName: personal.firstName || "",
                            middleName: personal.middleName || "",
                            lastName: personal.lastName || "",
                            experience: work.totalYrs
                                ? `${work.totalYrs} years`
                                : "",
                            position: work?.currentRole?.designation || "",
                            company: work?.currentRole?.orgName || "",
                            industry: work?.currentRole?.industry || "",
                            location: work?.currentRole?.location || "",
                            salary: compensation?.total
                                ? `₹ ${compensation.total} L / annum`
                                : "",
                            personImage: personal?.displayPic?.[0]?.path || null,
                            status: candidate?.status || null,
                        };
                    });

                    setCandidates(mapped);
                    setTotalEntries(res.data.totalCount || 0);
                    setTotalPages(res.data.pageCount || 1);
                } catch (err) {
                    console.error(err);
                    setCandidates([]);
                    setTotalEntries(0);
                    setTotalPages(0);
                } finally {
                    setLoad(false);
                }

                return;
            }

            setLoad(true);
            try {
                let res;

                if (activeTab === "shortlisted") {
                    res = await ClientService.getShortlistedCandidates(
                        selectedFolder.folderId,
                        currentPage,
                        PAGE_SIZE,
                        searchKeyword

                    );

                    const mapped = (res.shortlists || []).map(item => {
                        const candidate = item.candidate;
                        const personal = candidate?.candidate?.personalDetails || {};
                        const work = candidate?.candidate?.workExperience || {};
                        const compensation = candidate?.candidate?.compensationDetails || {};
                        const historyNodes =
                            item.shortlistHistory?.shortlistHistoryNodes || [];


                        '';
                        return {
                            candidateId: candidate.candidateId,
                            salutation: personal?.salutation || '',
                            firstName: personal?.firstName || '',
                            middleName: personal?.middleName || '',
                            lastName: personal?.lastName || '',
                            experience: work.totalYrs
                                ? `${work.totalYrs} years`
                                : "",

                            position: work?.currentRole?.designation || "",
                            company: work?.currentRole?.orgName || "",
                            industry: work?.currentRole?.industry || "",
                            location: work?.currentRole?.location || "",
                            salary: compensation?.total ? `₹ ${compensation.total} L / annum` : '',
                            personImage: personal?.displayPic?.[0]?.path || null,
                            status: candidate?.status || null,
                        };
                    });

                    setCandidates(mapped);
                    const total = res.totalCount || 0;
                    setTotalEntries(total);
                    setTotalPages(Math.ceil(total / PAGE_SIZE));

                } else {
                    res = await ClientFolderService.getCuratedCandidates(
                        clientId,
                        selectedFolder.folderId,
                        currentPage,
                        PAGE_SIZE,
                        searchKeyword

                    );

                    const mapped = (res.curatedList || []).map(item => {
                        const candidate = item.candidate;
                        const personal = candidate?.candidate?.personalDetails || {};
                        const work = candidate?.candidate?.workExperience || {};
                        const compensation = candidate?.candidate?.compensationDetails || {}

                        return {
                            candidateId: candidate.candidateId,
                            salutation: personal.salutation || "",
                            firstName: personal.firstName || "",
                            middleName: personal.middleName || "",
                            lastName: personal.lastName || "",
                            experience: work.totalYrs
                                ? `${work.totalYrs} years`
                                : "",
                            position: work?.currentRole?.designation || "",
                            company: work?.currentRole?.orgName || "",
                            industry: work?.currentRole?.industry || "",
                            location: work?.currentRole?.location || "",
                            salary: compensation?.total ? `₹ ${compensation.total} L / annum` : '',
                            personImage: personal?.displayPic?.[0]?.path || null,

                            status: candidate?.status || null
                        };
                    });

                    setCandidates(mapped);
                    const total = res.totalCount || 0;
                    setTotalEntries(total);
                    setTotalPages(Math.ceil(total / PAGE_SIZE));

                }
            } catch (err) {
                console.error("Error loading candidates", err);
                setCandidates([]);
                setTotalPages(0);
            } finally {
                setLoad(false);
            }
        };

        loadCandidates();
    }, [selectedFolder, currentPage, activeTab, searchKeyword, clientId, reloadCandidatesKey]);

    useEffect(() => {
        const fetchSubscriptionData = async () => {
            try {
                const response = await ClientService.activeSubscriptionCount(clientId);
                const subscriptionData = response.data;
                setTotalViews(subscriptionData.totalViews);
                setViewedCount(subscriptionData.viewedCount);
            } catch (error) {
                console.error('Error fetching subscription data:', error);
            }
        };

        fetchSubscriptionData();
    }, [clientId]);
    useEffect(() => {
        if (totalEntries === 0) {
            setStartEntry(0);
            setEndEntry(0);
            return;
        }

        setStartEntry(currentPage * PAGE_SIZE + 1);
        setEndEntry(
            Math.min((currentPage + 1) * PAGE_SIZE, totalEntries)
        );
    }, [currentPage, totalEntries]);


    const handleSearchResult = () => {
        navigate("/client/candidate-search-result");
    };
    useEffect(() => {
        if (searchInput === "") {
            setSearchKeyword("");
            setCurrentPage(0);
        }
    }, [searchInput]);

    useEffect(() => {
        const saved = sessionStorage.getItem("curatedShortlistedState");
        const restoreOnce = sessionStorage.getItem("restoreCuratedShortlisted");

        if (!saved || restoreOnce !== "true") {
            sessionStorage.removeItem("curatedShortlistedState");
            sessionStorage.removeItem("restoreCuratedShortlisted");
            setIsRestoring(false);
            return;
        }

        const {
            activeTab,
            applicantJobStatus,
            currentPage,
            selectedFolder,
            searchKeyword,
            searchInput
        } = JSON.parse(saved);

        setActiveTab(activeTab);
        setApplicantJobStatus(applicantJobStatus || "ACTIVE");
        setSelectedFolder(selectedFolder);
        setCurrentPage(currentPage);
        setSearchKeyword(searchKeyword);
        setSearchInput(searchInput);

        sessionStorage.removeItem("restoreCuratedShortlisted");

        setIsRestoring(false);
    }, []);


    const openFolderMenu = (e, folder) => {
        e.stopPropagation();
        setActiveFolderMenu(cur =>
            cur === folder.folderId ? null : folder.folderId
        );
    };

    const openRenamePopup = (folder) => {
        if (activeTab !== 'shortlisted') return;

        setFolderToRename(folder);
        setRenameValue(folder.folderName || folder.name || '');
        setShowRenamePopup(true);
        setActiveFolderMenu(null);
    };

    const handleRenameSave = async () => {
        if (!folderToRename || !renameValue.trim()) return;

        try {
            await ClientFolderService.renameFolder(
                clientId,
                folderToRename.folderId,
                renameValue.trim()
            );

            const res = await ClientFolderService.listShortlistedFolders(
                clientId,
                "SHORTLISTED",
                folderPage,
                PAGE_SIZE
            );
            setFolders(
                (res?.data?.folders || []).map(f => ({
                    folderId: f.folderId,
                    folderName: f.name
                }))
            );

            setShowRenamePopup(false);
            setFolderToRename(null);
        } catch (err) {
            console.error('Rename failed', err);
        }
    };

    const openDeleteConfirm = (folder) => {
        if (activeTab !== 'shortlisted') return;

        setFolderToDelete(folder);
        setShowDeletePopup(true);
        setActiveFolderMenu(null);
    };

    const handleDeleteConfirm = async () => {
        if (!folderToDelete) return;

        try {
            await ClientFolderService.deleteFolder(
                folderToDelete.folderId,
                clientId
            );

            const res = await ClientFolderService.listShortlistedFolders(
                clientId,
                "SHORTLISTED",
                folderPage,
                PAGE_SIZE
            );
            setFolders(
                (res?.data?.folders || []).map(f => ({
                    folderId: f.folderId,
                    folderName: f.name
                }))
            );

            if (selectedFolder?.folderId === folderToDelete.folderId) {
                setSelectedFolder(null);
                setCandidates([]);
                setCurrentPage(0);
            }

            setShowDeletePopup(false);
            setFolderToDelete(null);
        } catch (err) {
            console.error("Delete failed", err);

            if (err?.response?.status === 500) {
                setErrorMessage(
                    "This folder contains shortlisted candidates. Please move or remove the shortlisted candidates to another folder before deleting this folder."
                );

                setShowDeletePopup(false);
                setShowErrorPopup(true);
            }
        }
    };

    useEffect(() => {
        const close = () => setActiveFolderMenu(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, []);
    const triggerSearch = () => {
        setCurrentPage(0);
        setSearchKeyword(searchInput.trim());
    };

    const renderFolderPageNumbers = () => {
        const pageNumbers = [];
        const current = folderPage + 1;

        if (folderTotalPages <= 5) {
            for (let i = 1; i <= folderTotalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            pageNumbers.push(1);

            if (current > 3) {
                pageNumbers.push('...');
            }

            const start = Math.max(2, current - 1);
            const end = Math.min(folderTotalPages - 1, current + 1);

            for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
            }

            if (current < folderTotalPages - 2) {
                pageNumbers.push('...');
            }

            pageNumbers.push(folderTotalPages);
        }

        return pageNumbers;
    };

    return (
        <Container fluid className="container-styles" style={{ minHeight: "600px" }}>
            {load && (
                <div className="mycandidates-loader-container">
                    <div className="mycandidates-loader"></div>
                </div>
            )}

            {/* HEADER */}
            <div className="candidate-landing-page-header">
                <div className="searchbar-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                        type="text"
                        className="searchbar-input"
                        placeholder="Search Function etc."
                        onFocus={handleSearchResult}
                    />
                    <button className="search-button" onClick={handleSearchResult}>
                        Search
                    </button>
                </div>
            </div>

            <hr className="candidate-landing-page-hr" />

            {/* STATS */}
            <div className="candidate-landing-page-stats-upgrade">
                <div className="candidate-landing-page-upgrade-section">
                    <p>
                        You can increase the chances of finding the right candidate.
                        <a href="#"> Upgrade</a> now to Essential +
                    </p>
                </div>

                <div className="candidate-landing-page-stats-section">
                    <div className="candidate-landing-page-stat">
                        <span className="candidate-landing-page-stat-label">Alloted Candidates</span>
                        <span className="candidate-landing-page-count">{totalViews}</span>
                    </div>
                    <div className="candidate-landing-page-stat">
                        <span className="candidate-landing-page-stat-label">Viewed Candidates</span>
                        <span className="candidate-landing-page-count">{viewedCount}</span>
                    </div>
                </div>
            </div>

            <div className="candidate-tabs-container" style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                rowGap: "12px",
                margin: "20px 0",
                borderBottom: "1px solid #ddd",
                paddingBottom: 10,
                gap: "32px"
            }}>
                <button
                    className={`candidate-tab-btn ${activeTab === "curated" ? "active" : ""}`}
                    onClick={() => {
                        if (activeTab === "curated") return;
                        setLoad(true);
                        setSelectedFolder(null);
                        setCandidates([]);
                        setCurrentPage(0);
                        setFolderPage(0);
                        setApplicantJobStatus("ACTIVE");
                        setActiveTab("curated");
                    }} style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer', fontWeight: activeTab === 'curated' ? 700 : 500, color: activeTab === "curated" ? "#f35824" : "#2d3e50" }}
                >
                    Curated Candidates
                </button>

                <button
                    className={`candidate-tab-btn ${activeTab === "shortlisted" ? "active" : ""}`}
                    onClick={() => {
                        if (activeTab === "shortlisted") return;
                        setLoad(true);
                        setSelectedFolder(null);
                        setCandidates([]);
                        setCurrentPage(0);
                        setFolderPage(0);
                        setApplicantJobStatus("ACTIVE");
                        setActiveTab("shortlisted");
                    }} style={{ background: 'none', border: 'none', padding: 8, cursor: 'pointer', fontWeight: activeTab === 'shortlisted' ? 700 : 500, color: activeTab === "shortlisted" ? "#f35824" : "#2d3e50" }}
                >
                    Shortlisted Candidates
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <button
                        className={`candidate-tab-btn ${activeTab === "applicants" ? "active" : ""
                            }`}
                        onClick={() => {
                            if (activeTab === "applicants") return;

                            setSelectedFolder(null);
                            setCandidates([]);
                            setCurrentPage(0);
                            setFolderPage(0);
                            setApplicantJobStatus("ACTIVE");
                            setActiveTab("applicants");
                        }}
                        style={{
                            background: "none",
                            border: "none",
                            padding: 8,
                            cursor: "pointer",
                            fontWeight: activeTab === "applicants" ? 700 : 500,
                            color: activeTab === "applicants" ? "#f35824" : "#2d3e50",
                        }}
                    >
                        Job Applicants
                    </button>
                </div>
            </div>
            {selectedFolder && (
                <div className="folder-search-header">
                    {/* LEFT */}
                    <div className="folder-header-left">
                        <button
                            className="back-to-folders"
                            onClick={() => {
                                setSelectedFolder(null);
                                setCandidates([]);
                                setSearchInput("");
                                setSearchKeyword("");
                            }}

                        >
                            Back
                        </button>

                        <h4 className="selected-folder-name">
                            {activeTab === "applicants"
                                ? `${applicantJobStatus === "ACTIVE" ? "Active" : "Closed"} Job - ${selectedFolder.folderName}`
                                : selectedFolder.folderName}

                            <span className="folder-count">({totalEntries})</span>
                        </h4>

                    </div>

                    <div className="candidate-search-field">
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    triggerSearch();
                                }
                            }}
                        />
                        <FontAwesomeIcon
                            icon={faSearch}
                            className="search-icon"
                            style={{ cursor: "pointer" }}
                            onClick={triggerSearch}
                        />
                    </div>
                </div>
            )}

            {activeTab === "applicants" && !selectedFolder && (
                <div className="applicant-job-filter-row">

                    <button
                        className={
                            applicantJobStatus === "ACTIVE"
                                ? "active-filter-btn"
                                : "filter-btn"
                        }
                        onClick={() => {
                            setApplicantJobStatus("ACTIVE");
                            setFolderPage(0);
                            setSelectedFolder(null);
                        }}
                    >
                        Active Jobs
                    </button>

                    <button
                        className={
                            applicantJobStatus === "CLOSED"
                                ? "active-filter-btn"
                                : "filter-btn"
                        }
                        onClick={() => {
                            setApplicantJobStatus("CLOSED");
                            setFolderPage(0);
                            setSelectedFolder(null);
                        }}
                    >
                        Closed Jobs
                    </button>

                </div>
            )}

            {!selectedFolder && (
                <FolderGrid
                    folders={folders}
                    selectedFolder={selectedFolder}
                    onFolderClick={(folder) => {
                        setSelectedFolder(folder);
                        setCurrentPage(0);
                    }}
                    showMenu={activeTab === "shortlisted"}
                    activeFolderMenu={activeFolderMenu}
                    openFolderMenu={openFolderMenu}
                    openRenamePopup={openRenamePopup}
                    openDeleteConfirm={openDeleteConfirm}
                />
            )}

            {!selectedFolder && folderTotalPages > 1 && (
                <div className="d-flex justify-content-center my-3">
                    <div className="pagination-container">

                        <button
                            onClick={() => setFolderPage(0)}
                            disabled={folderPage === 0}
                        >
                            {'<<'}
                        </button>

                        <button
                            onClick={() =>
                                setFolderPage((prev) => Math.max(prev - 1, 0))
                            }
                            disabled={folderPage === 0}
                        >
                            {'<'}
                        </button>

                        {renderFolderPageNumbers().map((page, index) => (
                            <button
                                key={index}
                                className={`border rounded p-1 ${folderPage + 1 === page ? 'active' : ''
                                    }`}
                                onClick={() => {
                                    if (typeof page === 'number') {
                                        setFolderPage(page - 1);
                                    }
                                }}
                                disabled={typeof page !== 'number'}
                            >
                                {page}
                            </button>
                        ))}

                        <button
                            onClick={() =>
                                setFolderPage((prev) =>
                                    Math.min(prev + 1, folderTotalPages - 1)
                                )
                            }
                            disabled={folderPage >= folderTotalPages - 1}
                        >
                            {'>'}
                        </button>

                        <button
                            onClick={() => setFolderPage(folderTotalPages - 1)}
                            disabled={folderPage >= folderTotalPages - 1}
                        >
                            {'>>'}
                        </button>

                    </div>
                </div>
            )}


            {/* CANDIDATE CARDS */}
            {selectedFolder && (
                <CandidateGrid
                    loading={load}
                    candidates={candidates}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    tabType={activeTab.toUpperCase()}
                    preserveState={{
                        activeTab,
                        applicantJobStatus,
                        currentPage,
                        selectedFolder,
                        searchKeyword,
                        searchInput
                    }}
                    onCandidateMoved={() => {
                        setReloadCandidatesKey(prev => prev + 1);
                    }}
                    selectedFolder={selectedFolder}
                />
            )}

            {/* FALLBACK */}
            {!selectedFolder && folders.length === 0 && (
                <div className="candidate-landing-page-image-section">
                    <img
                        src={candidate_landing_page_image}
                        alt="empty"
                        className="candidate_landing_page_image"
                    />
                    <p className="candidate-landing-page-no-candidates">
                        {activeTab === "curated"
                            ? "Oops... No Curated Candidates yet"
                            : activeTab === "shortlisted"
                                ? "Oops... No Shortlisted Candidates yet"
                                : "Oops... No Job Listings yet"}
                    </p>
                </div>
            )}

            {showRenamePopup && activeTab === 'shortlisted' && (
                <div className="folder-modal-overlay">
                    <div className="folder-modal">
                        <h4>Rename Folder</h4>
                        <input
                            type="text"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                        />
                        <div className="modal-actions">
                            <button onClick={() => {
                                setShowRenamePopup(false);
                                setFolderToRename(null);
                            }}>
                                Cancel
                            </button>
                            <button onClick={handleRenameSave}>
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showDeletePopup && activeTab === 'shortlisted' && (
                <div className="folder-modal-overlay">
                    <div className="folder-modal">
                        <h4>Delete Folder</h4>
                        <p>
                            Are you sure you want to delete
                            <strong> "{folderToDelete?.folderName}"</strong>?
                        </p>
                        <div className="modal-actions">
                            <button
                                style={{ border: 'none', borderRadius: '50px' }}
                                onClick={() => {
                                    setShowDeletePopup(false);
                                    setFolderToDelete(null);
                                }}>
                                Cancel
                            </button>
                            <button
                                style={{ background: 'red', color: '#fff', border: 'none', borderRadius: '50px' }}
                                onClick={handleDeleteConfirm}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showErrorPopup && (
                <div className="delete-modal-overlay">
                    <div className="delete-modal">
                        <h5 style={{ fontWeight: '600' }}>Unable to Delete Folder</h5>
                        <p style={{ fontSize: '16px', color: '#2d3e50' }}>{errorMessage}</p>

                        <div className="modal-actions">
                            <button
                                onClick={() => {
                                    setShowErrorPopup(false);
                                    setErrorMessage("");
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </Container>
    );
};

export default CuratedShortlistedSearch;